import { useMemo } from 'react';
import { BarcodeFormat, DataMatrixWriter, QRCodeWriter, EncodeHintType } from '@zxing/library';
import { code128Widths, normFormat } from '../services/barcodeBatch';

/**
 * Zeichnet einen Barcode als SVG (schwarz auf weiß, mit Ruhezone) — scanbar vom Bildschirm.
 * Code 128 über den eigenen Encoder; andere 1D-Formate werden mit gleichem Inhalt als
 * Code 128 ausgegeben; Data Matrix + QR über die zxing-Writer.
 */
function matrixSvg(bm) {
  const w = bm.getWidth(), h = bm.getHeight();
  const q = 2; // Ruhezone in Modulen
  let d = '';
  for (let y = 0; y < h; y++) for (let x = 0; x < w; x++) if (bm.get(x, y)) d += `M${x + q} ${y + q}h1v1h-1z`;
  return { vb: `0 0 ${w + 2 * q} ${h + 2 * q}`, d };
}

export default function BarcodeBild({ text, format, maxHoehe = 260 }) {
  const f = normFormat(format);
  const svg = useMemo(() => {
    try {
      if (f === 'data_matrix' || f === 'qr_code') {
        const hints = new Map();
        if (f === 'qr_code') hints.set(EncodeHintType.MARGIN, 0);
        const bm = f === 'qr_code'
          ? new QRCodeWriter().encode(text, BarcodeFormat.QR_CODE, 0, 0, hints)
          : new DataMatrixWriter().encode(text, BarcodeFormat.DATA_MATRIX, 0, 0);
        return { kind: '2d', ...matrixSvg(bm) };
      }
      const widths = code128Widths(text);
      const q = 12;
      let x = q;
      let d = '';
      widths.forEach((w, i) => { if (i % 2 === 0) d += `M${x} 0h${w}v60h-${w}z`; x += w; });
      // ⚠️ Scanbarkeit: jedes Modul muss GANZE Gerätepixel breit sein — gestreckt auf „100 %"
      // verschwimmen lange Codes (DPD, ~230 Module) und sind nicht mehr lesbar (gemessen).
      const module = x + q;
      const dpr = (typeof window !== 'undefined' && window.devicePixelRatio) || 1;
      const verfuegbar = Math.max(200, Math.min(((typeof window !== 'undefined' && window.innerWidth) || 400) - 48, 1100));
      const k = Math.max(1, Math.floor((verfuegbar * dpr) / module)); // Gerätepixel je Modul
      return { kind: '1d', vb: `0 0 ${module} 60`, d, cssBreite: (module * k) / dpr };
    } catch {
      return null;
    }
  }, [text, f]);

  if (!svg) return <div style={{ color: '#e5484d', padding: 20 }}>⚠️ Code nicht darstellbar</div>;
  return (
    <div data-testid="barcode-bild" style={{ background: '#fff', padding: 12, borderRadius: 8, display: 'flex', justifyContent: 'center' }}>
      <svg viewBox={svg.vb} preserveAspectRatio={svg.kind === '1d' ? 'none' : 'xMidYMid meet'}
        shapeRendering="crispEdges"
        style={{ width: svg.kind === '1d' ? svg.cssBreite : '100%', height: svg.kind === '1d' ? Math.min(maxHoehe, 160) : maxHoehe, display: 'block' }}>
        <rect x="0" y="0" width="100%" height="100%" fill="#fff" />
        <path d={svg.d} fill="#000" />
      </svg>
    </div>
  );
}
